<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { WeekPickerFieldValue } from '@/components/pickerFields/WeekPickerField.vue';

import { reactive, ref, toRaw, watch } from 'vue';

import KRadioField from '@/components/fields/KRadioField.vue';
import MonthPickerField from '@/components/pickerFields/MonthPickerField.vue';
import WeekPickerField from '@/components/pickerFields/WeekPickerField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

type WeekRangeType = 'week';
type MonthRangeType = 'month';

export interface InitialData {
  rangeType?: WeekRangeType | MonthRangeType;
  monthDate?: string | null;
  weekRange?: WeekPickerFieldValue | null;
}

export interface ValidWeekData {
  rangeType: WeekRangeType;
  monthDate?: string | null;
  weekRange: WeekPickerFieldValue;
}
export interface ValidMonthData {
  rangeType: MonthRangeType;
  monthDate: string;
  weekRange?: WeekPickerFieldValue | null;
}

export type ValidData = ValidWeekData | ValidMonthData;

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const weekRangeType: WeekRangeType = 'week';
const monthRangeType: MonthRangeType = 'month';

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(
  props,
  formData,
  function (event) {
    emit('submit', event, toRaw(formData) as ValidData);
  },
  { watcher: false },
);

watch(
  () => props.initialData,
  (newInitialData) => {
    if (newInitialData.rangeType == null) {
      formData.rangeType = weekRangeType;
    } else {
      formData.rangeType = newInitialData.rangeType;
    }
    formData.monthDate = newInitialData.monthDate;
    formData.weekRange = newInitialData.weekRange;
  },
  { immediate: true },
);

function getRequiredRangeTypeValidationRule(
  rangeType: WeekRangeType | MonthRangeType,
) {
  return (value?: string | null): boolean | string => {
    if (formData.rangeType === rangeType) {
      return requiredValidationRule(value);
    }
    return true;
  };
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.driverReportGenerateDialogForm"
    data-testid="driverReportGenerateDialogForm"
    @submit.prevent="handleSubmit"
  >
    <v-radio-group
      v-model="formData.rangeType"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.radioGroup"
      inline
    >
      <KRadioField label="For week" :value="weekRangeType" />
      <KRadioField label="For month" :value="monthRangeType" />
    </v-radio-group>
    <WeekPickerField
      v-if="formData.rangeType === weekRangeType"
      v-model="formData.weekRange"
      label="Date range"
      :disabled="props.loading"
      :rules="[getRequiredRangeTypeValidationRule(weekRangeType)]"
      :class="$style.field"
      hide-details
    />
    <MonthPickerField
      v-if="formData.rangeType === monthRangeType"
      v-model="formData.monthDate"
      label="Date range"
      :disabled="props.loading"
      :rules="[getRequiredRangeTypeValidationRule(monthRangeType)]"
      :class="$style.field"
      hide-details
    />
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.driverReportGenerateDialogForm {
}
  .field {
  }
  .radioGroup {
  }
</style>
